import { useState } from 'preact/hooks';
import { route } from 'preact-router';
import { T } from '@/ui/tokens';
import { Spinner } from '@/ui/primitives/Spinner';
import { LegalModal } from '@/ui/primitives/LegalModal';
import { BrandX, BrandGoogle, BrandFacebook, BrandMail, BrandTelegram } from '@/ui/icons-brand';
import { config } from '@/config';
import { useEmailLogin, useEmailRegister, useForgotPassword, useSocialLogin, useTelegramLogin } from './useAuth';
import { signInWithGoogle } from '@/data/platform/google-auth';
import { telegram } from '@/data/platform/telegram';
import { termsHtml } from '@/features/legal/terms';
import { privacyHtml } from '@/features/legal/privacy';
import type { JSX } from 'preact';

type ProviderKey = 'x' | 'google' | 'facebook' | 'email' | 'telegram';

interface Provider {
  k: ProviderKey;
  l: string;
  bg: string;
  fg: string;
  border?: boolean;
  enabled: boolean;
  icon: JSX.Element;
}

export function LoginScreen() {
  const [mode, setMode] = useState<'choose' | 'email'>('choose');
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [telegramError, setTelegramError] = useState<string | null>(null);
  const socialLogin = useSocialLogin();
  const telegramLogin = useTelegramLogin();

  const telegramAvailable = telegram.isAvailable();
  const isEmailMode = mode === 'email';

  const providers: Provider[] = [
    { k: 'x', l: 'Continue with X', bg: '#000', fg: '#fff',
      enabled: config.auth.providers.x, icon: <BrandX/> },
    { k: 'google', l: 'Continue with Google', bg: '#fff', fg: '#0A0C0F',
      enabled: config.auth.providers.google, icon: <BrandGoogle/> },
    { k: 'facebook', l: 'Continue with Facebook', bg: '#1877F2', fg: '#fff',
      enabled: config.auth.providers.facebook, icon: <BrandFacebook/> },
    { k: 'telegram', l: 'Continue with Telegram', bg: '#229ED9', fg: '#fff',
      enabled: telegramAvailable, icon: <BrandTelegram/> },
    { k: 'email', l: 'Continue with Email', bg: 'transparent', fg: T.text, border: true,
      enabled: config.auth.providers.email, icon: <BrandMail/> },
  ];

  const onProvider = async (k: ProviderKey) => {
    if (k === 'email') { setMode('email'); return; }
    if (k === 'google') {
      setGoogleLoading(true);
      setGoogleError(null);
      try {
        const idToken = await signInWithGoogle();
        socialLogin.mutate({ provider: 'google', token: idToken });
      } catch (err: any) {
        console.error('[auth] Google sign-in failed:', err);
        setGoogleError(err?.message ?? 'Google sign-in failed. Try another method.');
      } finally {
        setGoogleLoading(false);
      }
      return;
    }
    if (k === 'telegram') {
      setTelegramError(null);
      const initData = telegram.initData();
      if (!initData) {
        setTelegramError('Telegram data not available. Try another method.');
        return;
      }
      telegramLogin.mutate({ initData }, {
        onError: (err: any) => {
          console.error('[auth] Telegram sign-in failed:', err);
          setTelegramError(err?.message ?? 'Telegram sign-in failed. Close and reopen the Mini App, then try again.');
        },
      });
      return;
    }
    alert(`OAuth flow for ${k} is not wired in the mini-app build.`);
  };

  return (
    <div style={{
      background: T.bg, position: 'fixed', inset: 0, width: '100%',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      paddingTop: 'var(--safe-top, 0px)', paddingBottom: 'var(--safe-bottom, 0px)',
    }}>
      <div style={{
        position: 'absolute', top: -120, left: '50%', transform: 'translateX(-50%)',
        width: 380, height: 380, borderRadius: '50%',
        background: `radial-gradient(circle, ${T.accent} 0%, transparent 65%)`,
        opacity: 0.18, filter: 'blur(40px)', pointerEvents: 'none',
      }}/>
      <div style={{
        position: 'absolute', bottom: -100, right: -60,
        width: 240, height: 240, borderRadius: '50%',
        background: `radial-gradient(circle, ${T.accent} 0%, transparent 60%)`,
        opacity: 0.10, filter: 'blur(30px)', pointerEvents: 'none',
      }}/>

      <div style={{
        flex: '1 1 auto', minHeight: 0,
        padding: isEmailMode ? '10px 24px 2px' : '14px 24px 8px',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isEmailMode ? 8 : 12 }}>
          <img
            src="/brand-logo.png"
            alt="Boostly"
            style={{
              width: isEmailMode ? 36 : 42,
              height: isEmailMode ? 36 : 42,
              borderRadius: isEmailMode ? 12 : 14,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text, letterSpacing: -0.5 }}>
              {config.brand.name}
            </div>
            <div style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase',
              letterSpacing: 1.5, fontWeight: 500 }}>{config.brand.tagline}</div>
          </div>
        </div>

        <div style={{
          fontSize: isEmailMode ? 26 : 30, fontWeight: 600, color: T.text,
          letterSpacing: 0, lineHeight: 1.05, marginBottom: isEmailMode ? 5 : 7,
        }}>
          Grow your reach,<br/>
          <span style={{
            background: `linear-gradient(135deg, ${T.accent} 0%, ${T.accent2} 100%)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>earn coins back.</span>
        </div>
        <p style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5,
          margin: 0, maxWidth: 320 }}>
          Buy real engagement across Telegram, Instagram, X & LinkedIn. Pay in coins,
          earn coins from referrals.
        </p>

        <div style={{
          marginTop: isEmailMode ? 8 : 10, padding: isEmailMode ? '8px 12px' : '9px 14px',
          borderRadius: 14,
          background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.line}`,
          textAlign: 'center',
        }}>
          <span style={{
            fontSize: 13, fontWeight: 600,
            background: `linear-gradient(135deg, ${T.accent} 0%, ${T.accent2} 100%)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>Boost smarter, grow faster.</span>
        </div>
      </div>

      <div style={{
        flex: '0 0 auto', minHeight: 0,
        padding: isEmailMode ? '8px 24px 12px' : '10px 24px 14px',
        display: 'flex', flexDirection: 'column', gap: isEmailMode ? 7 : 8,
      }}>
        {googleError && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#ef4444', fontSize: 13, lineHeight: 1.4,
          }}>
            {googleError}
          </div>
        )}

        {telegramError && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#ef4444', fontSize: 13, lineHeight: 1.4,
          }}>
            {telegramError}
          </div>
        )}

        {mode === 'choose' && providers.filter(p => p.enabled).map(p => {
          const isLoading =
            (p.k === 'google' && googleLoading) ||
            (p.k === 'telegram' && telegramLogin.isPending) ||
            (p.k !== 'google' && p.k !== 'telegram' && p.k !== 'email' && socialLogin.isPending);
          return (
            <button key={p.k} onClick={() => onProvider(p.k)}
              disabled={isLoading}
              style={{
                padding: '12px 16px', borderRadius: 14,
                background: p.bg, color: p.fg,
                border: p.border ? `1px solid ${T.line2}` : 'none',
                fontFamily: T.font, fontSize: 14, fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                display: 'flex', alignItems: 'center', gap: 12,
                minHeight: 44,
              }}>
              <span style={{ width: 24, display: 'inline-flex', justifyContent: 'center' }}>
                {isLoading ? <Spinner size={16}/> : p.icon}
              </span>
              <span style={{ flex: 1, textAlign: 'left' }}>{p.l}</span>
            </button>
          );
        })}

        {mode === 'email' && <EmailForm onCancel={() => setMode('choose')}/>}

        <p style={{ margin: isEmailMode ? '2px 0 0' : '6px 0 0', fontSize: 11, color: T.textMute, lineHeight: 1.4,
          textAlign: 'center' }}>
          By continuing you agree to our{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); setLegalModal('terms'); }}
            style={{ color: T.textDim, textDecoration: 'underline',
            textUnderlineOffset: 3 }}>Terms</a> &{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); setLegalModal('privacy'); }}
            style={{ color: T.textDim, textDecoration: 'underline',
            textUnderlineOffset: 3 }}>Privacy Policy</a>.
        </p>
      </div>

      {legalModal === 'terms' && <LegalModal title="Terms of Service" html={termsHtml} onClose={() => setLegalModal(null)}/>}
      {legalModal === 'privacy' && <LegalModal title="Privacy Policy" html={privacyHtml} onClose={() => setLegalModal(null)}/>}
    </div>
  );
}

function EmailForm({ onCancel }: { onCancel: () => void }) {
  const [formMode, setFormMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useEmailLogin();
  const register = useEmailRegister();
  const forgot = useForgotPassword();
  const submitting = login.isPending || register.isPending || forgot.isPending;
  const error = (login.error || register.error || forgot.error) as Error | null;
  const isForgot = formMode === 'forgot';
  const isSignUp = formMode === 'signup';

  const submit = (e: Event) => {
    e.preventDefault();
    if (isForgot) {
      forgot.mutate({ email });
      return;
    }
    if (isSignUp) register.mutate({ email, password });
    else login.mutate({ email, password });
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 12,
    background: T.surface, border: `1px solid ${T.line}`,
    color: T.text, fontFamily: T.font, fontSize: 14, outline: 'none',
    boxSizing: 'border-box' as const,
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {isForgot && (
        <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5, marginBottom: 2 }}>
          Enter your account email and we will send a reset code if it exists.
        </div>
      )}
      <input
        type="email"
        value={email}
        onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
        placeholder="Email"
        required
        style={inputStyle}
      />
      {!isForgot && (
        <input
          type="password"
          value={password}
          onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
          placeholder="Password (min 6 chars)"
          required
          minLength={6}
          style={inputStyle}
        />
      )}
      {error && (
        <div style={{ fontSize: 12, color: T.danger }}>
          {error.message}
        </div>
      )}
      {forgot.isSuccess && (
        <div style={{ fontSize: 12, color: T.ok }}>
          If the email exists, a reset link has been sent.
        </div>
      )}
      {isForgot && (
        <button type="button" onClick={() => route(config.routes.resetPasswordBase)} style={{
          background: 'none', border: 'none', color: T.textDim,
          fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
        }}>
          I have a reset token
        </button>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => {
          if (isForgot) {
            forgot.reset();
            setFormMode('login');
            return;
          }
          onCancel();
        }} style={{
          flex: 1, padding: '12px', borderRadius: 12,
          background: 'transparent', color: T.text, border: `1px solid ${T.line2}`,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Back</button>
        <button type="submit" disabled={submitting} style={{
          flex: 2, padding: '12px', borderRadius: 12, background: T.accent,
          color: T.accentInk, border: 'none', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', opacity: submitting ? 0.7 : 1,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          {submitting && <Spinner size={14}/>}
          {isForgot ? 'Send reset link' : isSignUp ? 'Create account' : 'Sign in'}
        </button>
      </div>
      {!isForgot && !isSignUp && (
        <button type="button" onClick={() => {
          login.reset();
          register.reset();
          forgot.reset();
          setFormMode('forgot');
        }} style={{
          background: 'none', border: 'none', color: T.textDim,
          fontSize: 12, cursor: 'pointer', textDecoration: 'underline', marginTop: 2,
        }}>
          Forgot password?
        </button>
      )}
      <button type="button" onClick={() => {
        login.reset();
        register.reset();
        forgot.reset();
        setFormMode(isSignUp ? 'login' : 'signup');
      }} style={{
        background: 'none', border: 'none', color: T.textDim,
        fontSize: 12, cursor: 'pointer', textDecoration: 'underline', marginTop: 4,
      }}>
        {isSignUp ? 'I already have an account' : 'New here? Create an account'}
      </button>
    </form>
  );
}
